import HMAC from 'crypto-js/hmac-sha256'
import HexEncoder from 'crypto-js/enc-hex'
import Utf8Encoder from 'crypto-js/enc-utf8'
import Base64Encoder from 'crypto-js/enc-base64url'
import { joinURL } from 'ufo'
import { encode } from 'url-safe-base64'
import type {
  PictureProcessingOptions,
  PictureExtension,
  PictureSizesOptions,
} from 'img-proxy'

/** @docs https://docs.imgproxy.net/signing_the_url */

/** Получение сигнатуры — зашифрованного в Base64 результата HMAC-вычисления от ссылки
 * @param processingPath - ссылка на изображение
 * @param secret - секретный ключ, строка
 * @param salt - соль
 * @returns Зашифрованная в URL-Safe Base64 строка
 */
const getProcessingPathSignature = (
  processingPath: string,
  secret: string,
  salt: string,
) => {
  const payload = HexEncoder.parse(salt)

  payload.concat(Utf8Encoder.parse(processingPath))

  const hmac = HMAC(payload, HexEncoder.parse(secret))

  const signature = encode(Base64Encoder.stringify(hmac)).replace(/\./g, '')

  if (signature.endsWith('=')) return signature.slice(0, -1)

  return signature
}

/** Получениех закодированной в Base64 ссылки на изображение
 * @param url - ссылка на изображение
 * @returns Зашифрованная в Base64 строка
 */
const getEncodedImageUrl = (url: string) => {
  const utf8Words = Utf8Encoder.parse(url)
  const base64 = Base64Encoder.stringify(utf8Words)

  return encode(base64).replace(/\./g, '')
}

const stringifyProcessingOptions = (options?: PictureProcessingOptions) =>
  options
    ? Object.entries(options).reduce(
        (acc, [key, value]) => [acc, [key, value].join(':')].join('/'),
        '',
      )
    : ''

/**  Получение закодированной ссылки с опциями трансформирования
 * @param originalImageUrl - ссылка на изображение
 * @param options
 * @param extension - расширение
 */
// TODO: Добавить обработку опций
const getProcessingPath = (
  originalImageUrl: string,
  extension: PictureExtension,
  options?: PictureProcessingOptions,
) => {
  const optionsAString = stringifyProcessingOptions(options)

  return (
    optionsAString +
    '/' +
    getEncodedImageUrl(originalImageUrl) +
    `.${extension}`
  )
}

/** Получение финальной ссылки на изображение
 * @param originalImageUrl - ссылка на изображение
 * @param imgProxyBaseUrl
 * @param imgProxySalt
 * @param imgProxySecret
 * @param extension
 * @param options
 */
const getPictureUrl = (
  originalImageUrl: string,
  { imgProxyBaseUrl, imgProxySalt, imgProxySecret }: Dictionary<string>,
  extension: PictureExtension,
  options?: PictureProcessingOptions,
) => {
  const processingPath = getProcessingPath(originalImageUrl, extension, options)

  return joinURL(
    imgProxyBaseUrl,
    getProcessingPathSignature(processingPath, imgProxySecret, imgProxySalt),
    processingPath,
  )
}

const usePicture = () => {
  const { imgProxyBaseUrl, imgProxySalt, imgProxySecret } =
    useRuntimeConfig().public

  try {
    if (!imgProxyBaseUrl || !imgProxySalt || !imgProxySecret) {
      throw new Error(
        'IMGPROXY variables is not defined. Please define NUXT_PUBLIC_IMGPROXY_BASEURL, NUXT_PUBLIC_IMGPROXY_SALT and NUXT_PUBLIC_IMGPROXY_SECRET variables in your .env file.',
      )
    }
    const getImageProxyUrl = (
      imageUrl: string,
      options?: PictureProcessingOptions,
      extension: PictureExtension = 'webp',
    ) =>
      getPictureUrl(
        imageUrl,
        { imgProxyBaseUrl, imgProxySalt, imgProxySecret },
        extension,
        options,
      )

    const containerSizedImageOptions: PictureSizesOptions = {
      tablet: { width: 750 },
      md: { width: 710 },
      xs: { width: 380 },
    }

    return { getImageProxyUrl, containerSizedImageOptions }
  } catch (e) {
    console.warn(e)
    const getImageProxyUrl = (imageUrl: string) => imageUrl

    return { getImageProxyUrl }
  }
}

export { usePicture }
